import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Edit2, Trash2, Copy, RefreshCw, Search,
  AlertTriangle, XCircle, ChevronLeft, ChevronRight, Calendar, Database
} from 'lucide-react';
import {
  listExpenses, updateExpense, deleteExpense, createExpense,
  duplicateExpenseToScenario, recalculateKPIs
} from '../../services/financialPlanningService';
import { getAzureDataFreshness } from '../../services/finopsService';
import FinancialExpenseModal from './FinancialExpenseModal';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const formatCurrency = (amount, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount || 0);

// ── Mês helpers (formato canônico: 'YYYY-MM', alinhado a financial_expenses.reference_month NVARCHAR(7)) ──
// Gate 1: mês corrente calculado em America/Sao_Paulo (não UTC) — evita virada
// errada de mês entre 21h e 00h horário de Brasília.
const todayMonthKey = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  return `${y}-${m}`;
};

const shiftMonth = (key, delta) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, (m - 1) + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (key, lang = 'pt') => {
  const [y, m] = key.split('-').map(Number);
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const monthName = new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long' });
  return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${y}`;
};

const Badge = ({ label, color = 'gray' }) => {
  const colors = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    blue: 'bg-blue-100 text-blue-800',
    purple: 'bg-purple-100 text-purple-800',
    gray: 'bg-gray-100 text-gray-700',
    gold: 'bg-amber-100 text-amber-800',
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${colors[color] || colors.gray}`}>{label}</span>;
};

const KPICard = ({ label, value, sub, accent = BRAND_GREEN }) => (
  <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
    <div className="text-lg font-extrabold mt-1" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const FinancialExpensesTable = ({ onKPIsUpdated }) => {
  const todayKey = todayMonthKey();
  const [selectedMonth, setSelectedMonth] = useState(todayKey); // 'YYYY-MM'
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ open: false, expense: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Filtros secundários (mês foi promovido a navegador no header)
  const [filters, setFilters] = useState({ category: '', scenario_type: '', source: '', product: '' });
  const [search, setSearch] = useState('');
  const [freshness, setFreshness] = useState(null); // Gate 1 — verdade operacional
  const { lang } = useGovernanceLang();
  const T = {
    pt: {
      title: 'Despesas', subtitle: 'Competência mensal • reference_month',
      records: (n, total) => `${n} registro(s) • Total: ${total}`,
      reload: 'Recarregar', newExpense: 'Nova Despesa',
      searchPlaceholder: 'Buscar por nome, descrição ou vendor...',
      allCategories: 'Todas categorias',
      allScenarios: 'Todos cenários', allSources: 'Todas origens',
      tools: 'Ferramentas', payroll: 'Pessoal', other: 'Outros',
      actual: 'Real', planned: 'Planejado', conservative: 'Conservador', aggressive: 'Agressivo',
      payrollSrc: 'Folha',
      colName: 'Nome', colCategory: 'Categoria', colProduct: 'Produto',
      colAmount: 'Valor', colMonth: 'Mês', colScenario: 'Cenário', colTags: 'Tags', colActions: 'Ações',
      tagDemo: 'Demo', tagAzure: 'Azure', tagRecurring: 'Recorrente', tagCredito: 'Crédito',
      emptyTitle: (m) => `Nenhuma despesa em ${m}`,
      emptyDesc: 'Adicione a primeira despesa deste mês ou ajuste os filtros.',
      addInMonth: (m) => `Adicionar despesa em ${m}`,
      deleteTitle: 'Cancelar despesa?',
      deleteDesc: 'A despesa será marcada como cancelada (soft-delete). Pode ser revertida editando o status.',
      no: 'Não', yesCancel: 'Sim, cancelar',
      prev: 'Mês anterior', next: 'Próximo mês', today: 'Mês atual',
      kpiTotal: 'Total do mês', kpiCount: 'Lançamentos',
      kpiCash: 'Saída de caixa', kpiCredit: 'Crédito Azure',
      kpiCashSub: 'fonte_pagamento = caixa', kpiCreditSub: 'fonte_pagamento = credito_azure',
    },
    en: {
      title: 'Expenses', subtitle: 'Monthly competence • reference_month',
      records: (n, total) => `${n} record(s) • Total: ${total}`,
      reload: 'Reload', newExpense: 'New Expense',
      searchPlaceholder: 'Search by name, description or vendor...',
      allCategories: 'All categories',
      allScenarios: 'All scenarios', allSources: 'All sources',
      tools: 'Tools', payroll: 'Payroll', other: 'Other',
      actual: 'Actual', planned: 'Planned', conservative: 'Conservative', aggressive: 'Aggressive',
      payrollSrc: 'Payroll',
      colName: 'Name', colCategory: 'Category', colProduct: 'Product',
      colAmount: 'Amount', colMonth: 'Month', colScenario: 'Scenario', colTags: 'Tags', colActions: 'Actions',
      tagDemo: 'Demo', tagAzure: 'Azure', tagRecurring: 'Recurring', tagCredito: 'Credit',
      emptyTitle: (m) => `No expenses in ${m}`,
      emptyDesc: 'Add the first expense for this month or adjust filters.',
      addInMonth: (m) => `Add expense in ${m}`,
      deleteTitle: 'Cancel expense?',
      deleteDesc: 'The expense will be marked as cancelled (soft-delete). Can be reverted by editing the status.',
      no: 'No', yesCancel: 'Yes, cancel',
      prev: 'Previous month', next: 'Next month', today: 'Current month',
      kpiTotal: 'Month total', kpiCount: 'Entries',
      kpiCash: 'Cash outflow', kpiCredit: 'Azure credit',
      kpiCashSub: 'fonte_pagamento = caixa', kpiCreditSub: 'fonte_pagamento = credito_azure',
    },
  }[lang] || {};

  const monthLabel = formatMonthLabel(selectedMonth, lang);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await listExpenses({ ...cleanFilters, referenceMonth: selectedMonth });
      setExpenses(res.data || []);
    } catch (err) {
      setError(err.message || 'Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [filters, selectedMonth]);

  useEffect(() => { load(); }, [load]);

  // Gate 1 — busca única da atualidade dos dados (não bloqueia a tabela)
  useEffect(() => {
    getAzureDataFreshness()
      .then(res => setFreshness(res?.data || null))
      .catch(() => setFreshness(null));
  }, []);

  const handleSave = async (payload, id) => {
    if (id) {
      await updateExpense(id, payload);
    } else {
      await createExpense(payload);
    }
    load();
    // Recalculate KPIs
    try {
      const kpis = await recalculateKPIs(payload.reference_month, payload.scenario_type);
      if (onKPIsUpdated) onKPIsUpdated(kpis);
    } catch (e) { /* non-fatal */ }
  };

  const handleDelete = async (id) => {
    try {
      await deleteExpense(id);
      setConfirmDelete(null);
      load();
    } catch (err) { setError(err.message); }
  };

  const handleDuplicate = async (id, scenario) => {
    try {
      await duplicateExpenseToScenario(id, scenario);
      load();
    } catch (err) { setError(err.message); }
  };

  const filtered = expenses.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.name || '').toLowerCase().includes(q) ||
           (e.description || '').toLowerCase().includes(q) ||
           (e.vendor || '').toLowerCase().includes(q);
  });

  // ── KPIs por mês (excluem cancelled). Caixa vs Crédito Azure derivam de fonte_pagamento. ──
  const kpis = useMemo(() => {
    const active = filtered.filter(e => e.status !== 'cancelled');
    const sumAll = active.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const cash = active
      .filter(e => (e.fonte_pagamento || 'caixa') !== 'credito_azure')
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const creditBrl = active
      .filter(e => e.fonte_pagamento === 'credito_azure')
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const creditUsd = active
      .filter(e => e.fonte_pagamento === 'credito_azure')
      .reduce((s, e) => s + (Number(e.valor_usd) || 0), 0);
    return { sumAll, cash, creditBrl, creditUsd, count: active.length };
  }, [filtered]);

  return (
    <div className="space-y-4">
      {/* ── Header: título + navegador mensal + ações ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {T.title} — <span style={{ color: BRAND_GREEN }}>{monthLabel}</span>
          </h3>
          <p className="text-xs text-gray-500">{T.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 border rounded-lg hover:bg-gray-50" title={T.reload}><RefreshCw size={16} /></button>
          <button
            onClick={() => setModal({ open: true, expense: null })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            <Plus size={16} /> {T.newExpense}
          </button>
        </div>
      </div>

      {/* ── Navegador mensal ── */}
      <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 flex items-center justify-between gap-3">
        <button
          onClick={() => setSelectedMonth(prev => shiftMonth(prev, -1))}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          title={T.prev}
        >
          <ChevronLeft size={16} />
          <span className="hidden sm:inline">{T.prev}</span>
        </button>
        <div className="flex items-center gap-2">
          <Calendar size={16} style={{ color: BRAND_GOLD }} />
          <span className="font-bold text-gray-900">{monthLabel}</span>
          <span className="text-xs text-gray-400">({selectedMonth})</span>
          {selectedMonth !== todayKey && (
            <button
              onClick={() => setSelectedMonth(todayKey)}
              className="ml-2 px-2 py-0.5 text-[11px] font-bold rounded-full border"
              style={{ borderColor: BRAND_GOLD, color: BRAND_GOLD }}
              title={T.today}
            >
              {T.today}
            </button>
          )}
        </div>
        <button
          onClick={() => setSelectedMonth(prev => shiftMonth(prev, +1))}
          className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
          title={T.next}
        >
          <span className="hidden sm:inline">{T.next}</span>
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ── Gate 1: banner de atualidade dos dados ── */}
      {freshness && (() => {
        const azureLastDate = freshness.azure?.last_usage_date || null;      // 'YYYY-MM-DD'
        const azureLastMonth = azureLastDate ? azureLastDate.slice(0, 7) : null;
        const consolidatedUpTo = freshness.consolidation?.last_reference_month || null;
        const fxOk = freshness.fx?.covers_current_month;
        const unmapped = freshness.unmapped_current_month;
        const monthHasNoAzure = azureLastMonth && selectedMonth > azureLastMonth;
        const fmtDate = (iso) => iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—';
        const isPt = lang !== 'en';
        return (
          <div
            className={`rounded-xl border px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${monthHasNoAzure ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
          >
            <span className="flex items-center gap-1.5 font-semibold">
              <Database size={13} />
              {isPt
                ? `Dados Azure atualizados até ${fmtDate(azureLastDate)}`
                : `Azure data updated through ${fmtDate(azureLastDate)}`}
            </span>
            <span>
              {isPt
                ? `Última consolidação: ${consolidatedUpTo || '—'}`
                : `Last consolidation: ${consolidatedUpTo || '—'}`}
            </span>
            <span>
              {isPt
                ? `${freshness.azure?.subscriptions_count ?? 0} assinatura(s)`
                : `${freshness.azure?.subscriptions_count ?? 0} subscription(s)`}
            </span>
            {typeof unmapped === 'number' && unmapped > 0 && (
              <span className="font-semibold text-amber-700">
                {isPt ? `${unmapped} item(ns) sem classificação` : `${unmapped} unclassified item(s)`}
              </span>
            )}
            {!fxOk && (
              <span className="font-semibold text-amber-700">
                {isPt
                  ? `PTAX disponível até ${fmtDate(freshness.fx?.last_rate_date)}`
                  : `FX (PTAX) available through ${fmtDate(freshness.fx?.last_rate_date)}`}
              </span>
            )}
            {monthHasNoAzure && (
              <span className="basis-full font-bold flex items-center gap-1">
                <AlertTriangle size={13} />
                {isPt
                  ? `${monthLabel} ainda não possui dados Azure importados — último período: até ${fmtDate(azureLastDate)}. Os totais abaixo NÃO incluem consumo Azure deste mês.`
                  : `${monthLabel} has no imported Azure data yet — last period: through ${fmtDate(azureLastDate)}. Totals below do NOT include this month's Azure usage.`}
              </span>
            )}
          </div>
        );
      })()}

      {/* ── KPI cards do mês selecionado ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPICard label={T.kpiTotal} value={formatCurrency(kpis.sumAll)} sub={T.records(kpis.count, formatCurrency(kpis.sumAll))} />
        <KPICard label={T.kpiCount} value={kpis.count} accent={BRAND_GOLD} />
        <KPICard label={T.kpiCash} value={formatCurrency(kpis.cash)} sub={T.kpiCashSub} />
        <KPICard
          label={T.kpiCredit}
          value={formatCurrency(kpis.creditBrl)}
          sub={kpis.creditUsd ? `${T.kpiCreditSub} • USD ${kpis.creditUsd.toFixed(2)}` : T.kpiCreditSub}
          accent="#1d4ed8"
        />
      </div>

      {/* ── Filtros secundários (sem mês — agora vive no navegador) ── */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={T.searchPlaceholder}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <select value={filters.category} onChange={e => setFilters(f => ({...f, category: e.target.value}))} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">{T.allCategories}</option>
          <option value="cloud">Cloud</option>
          <option value="tools">{T.tools}</option>
          <option value="payroll">{T.payroll}</option>
          <option value="legal">Legal</option>
          <option value="marketing">Marketing</option>
          <option value="other">{T.other}</option>
        </select>
        <select value={filters.scenario_type} onChange={e => setFilters(f => ({...f, scenario_type: e.target.value}))} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">{T.allScenarios}</option>
          <option value="actual">{T.actual}</option>
          <option value="planned">{T.planned}</option>
          <option value="demo">Demo</option>
          <option value="base">Base</option>
          <option value="conservative">{T.conservative}</option>
          <option value="aggressive">{T.aggressive}</option>
        </select>
        <select value={filters.source} onChange={e => setFilters(f => ({...f, source: e.target.value}))} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">{T.allSources}</option>
          <option value="manual">Manual</option>
          <option value="azure_finops">Azure FinOps</option>
          <option value="payroll">{T.payrollSrc}</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 px-4 py-2 rounded-lg text-sm">
          <AlertTriangle size={16} /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500"><XCircle size={14} /></button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4].map(i => <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
          <Calendar size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-lg font-medium text-gray-700">{T.emptyTitle(monthLabel)}</p>
          <p className="text-sm text-gray-500 mb-4">{T.emptyDesc}</p>
          <button
            onClick={() => setModal({ open: true, expense: null })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            <Plus size={16} /> {T.addInMonth(monthLabel)}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">{T.colName}</th>
                  <th className="px-3 py-3">{T.colCategory}</th>
                  <th className="px-3 py-3">{T.colProduct}</th>
                  <th className="px-3 py-3 text-right">{T.colAmount}</th>
                  <th className="px-3 py-3">{T.colMonth}</th>
                  <th className="px-3 py-3">{T.colScenario}</th>
                  <th className="px-3 py-3">{T.colTags}</th>
                  <th className="px-3 py-3 text-right">{T.colActions}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(exp => (
                  <tr key={exp.id} className={`border-t hover:bg-gray-50 ${exp.status === 'cancelled' ? 'opacity-50 line-through' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]">{exp.name || exp.description || '—'}</div>
                      {exp.vendor && <div className="text-xs text-gray-400">{exp.vendor}</div>}
                    </td>
                    <td className="px-3 py-3 text-gray-700 capitalize">{exp.category}</td>
                    <td className="px-3 py-3 text-gray-600 text-xs">{exp.product || '—'}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold" style={{ color: BRAND_GREEN }}>
                      {formatCurrency(exp.amount, exp.currency)}
                    </td>
                    <td className="px-3 py-3 text-gray-600">{exp.reference_month || '—'}</td>
                    <td className="px-3 py-3">
                      <Badge
                        label={exp.scenario_type}
                        color={exp.scenario_type === 'actual' ? 'green' : exp.scenario_type === 'demo' ? 'gold' : 'blue'}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {exp.is_demo ? <Badge label={T.tagDemo} color="gold" /> : null}
                        {exp.source === 'azure_finops' || exp.category === 'cloud' ? <Badge label={T.tagAzure} color="blue" /> : null}
                        {exp.fonte_pagamento === 'credito_azure' ? <Badge label={T.tagCredito} color="purple" /> : null}
                        {exp.is_recurring ? <Badge label={T.tagRecurring} color="purple" /> : null}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setModal({ open: true, expense: exp })} className="p-1.5 hover:bg-gray-100 rounded" title="Editar">
                          <Edit2 size={14} className="text-gray-500" />
                        </button>
                        <button onClick={() => handleDuplicate(exp.id, 'base')} className="p-1.5 hover:bg-gray-100 rounded" title="Duplicar para cenário Base">
                          <Copy size={14} className="text-gray-500" />
                        </button>
                        <button onClick={() => setConfirmDelete(exp.id)} className="p-1.5 hover:bg-red-50 rounded" title="Cancelar">
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
            <h4 className="font-bold text-gray-900 mb-2">{T.deleteTitle}</h4>
            <p className="text-sm text-gray-500 mb-4">{T.deleteDesc}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border rounded-lg text-sm">{T.no}</button>
              <button onClick={() => handleDelete(confirmDelete)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold">{T.yesCancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — pré-popula reference_month com o mês selecionado quando criando */}
      {modal.open && (
        <FinancialExpenseModal
          expense={modal.expense}
          defaultReferenceMonth={selectedMonth}
          onClose={() => setModal({ open: false, expense: null })}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default FinancialExpensesTable;
